import React from 'react';
import { Settings } from '../../Settings';
import WalletCouponList from '../molecules/WalletCouponList';
import {WalletCoupon} from '../atoms/WalletCouponCard';
import AddToWalletButton from '../../components/AddToWalletButton';
// @ts-ignore
import QRCode from 'react-qr-code';
import LoadingSpinner from '../../LoadingSpinner';

type WalletDownloadViewProps = {
    showApple: boolean
    showGoogle: boolean
    appleUrl?: string
    googleUrl?: string
    preferredUrl?: string
    showQRCode?: boolean
    coupons?: Array<WalletCoupon>
    envCopy?: { iosChrome?: boolean, unknown?: boolean }
    onUnsupported?: () => void
    autoDetectAndDownload?: boolean
    isDownloading?: boolean
    downloadingMessage?: string
    onAppleDownload?: () => void
    onGoogleDownload?: () => void
}

export default function WalletDownloadView(props: WalletDownloadViewProps) {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const appleRef = React.useRef<HTMLDivElement | null>(null);
    const googleRef = React.useRef<HTMLDivElement | null>(null);
    const [isStacked, setIsStacked] = React.useState<boolean>(false);

    React.useEffect(() => {
        const measure = () => {
            if (!containerRef.current) return;
            // Calculate whether two columns can fit side-by-side based on
            // their declared minWidth and the gap + divider width.
            const containerWidth = containerRef.current.clientWidth;
            const minColumnWidth = 320; // matches inline style minWidth for columns
            const gapBetweenColumns = 40; // matches container gap
            const dividerWidthWithMargin = 17; // 1px + 8px margins on each side
            const threshold = (props.showApple && props.showGoogle)
                ? (minColumnWidth * 2 + gapBetweenColumns + dividerWidthWithMargin)
                : 0;
            setIsStacked(threshold > 0 ? containerWidth < threshold : false);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [props.showApple, props.showGoogle]);
    return (
        <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            background: '#FFFFFF',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
        }}>
            {props.coupons && props.coupons.length > 0 && (
                <WalletCouponList coupons={props.coupons} />
            )}

            <div style={{
                borderTop: '3px dotted #D1D5DB',
                margin: '0 0 16px 0'
            }}/>

            <div className='bLoyal-center' style={{display: 'flex', flexDirection: 'column', gap: '12px', paddingBottom: '24px'}}>
                {props.envCopy?.iosChrome && (
                    <div style={{color:'#374151', fontSize:'0.95rem'}}>
                        You're on iOS Chrome—please open in Safari to add to Apple Wallet.
                    </div>
                )}
                {props.envCopy?.unknown && (
                    <div style={{color:'#6B7280', fontSize:'0.95rem'}}>
                        Digital wallet compatibility could not be detected on this browser.
                    </div>
                )}

                {props.isDownloading ? (
                    <div className='bLoyal-center' style={{display:'flex', flexDirection:'column', alignItems:'center', gap:12, padding:'12px 0'}}>
                        <LoadingSpinner />
                        {props.downloadingMessage && (
                            <div style={{color:'#374151', fontSize:'1rem', textAlign:'center', maxWidth:640}}>{props.downloadingMessage}</div>
                        )}
                    </div>
                ) : (
                <>
                    {props.showQRCode === false && (
                        <div className='bLoyal-center' style={{display:'flex', flexDirection:'column', gap:20}}>
                            {props.showApple && props.appleUrl && (
                                <div className='bLoyal-center' style={{display:"flex", gap:16}}>
                                    <a href={props.appleUrl} download aria-label="Add to Apple Wallet" onClick={props.onAppleDownload}>
                                        <img src={`${Settings.BaseUrl}/wallet-badges/apple.svg`} alt="Add to Apple Wallet" style={{height:28, transform:'scale(5)', transformOrigin:'center center', padding:'10px 0'}} />
                                    </a>
                                </div>
                            )}
                            {props.showGoogle && props.googleUrl && (
                                <a href={props.googleUrl} aria-label="Save to Google Wallet" onClick={props.onGoogleDownload}>
                                    <img src={`${Settings.BaseUrl}/wallet-badges/google.svg`} alt="Save to Google Wallet" style={{height:28, transform:'scale(2.4)', transformOrigin:'center center', padding:'6px 0'}} />
                                </a>
                            )}
                        </div>
                    )}

                    {props.showQRCode !== false && (
                        <div ref={containerRef} style={{display:'flex', gap:40, justifyContent:'center', flexWrap:'wrap'}}>
                            {props.showApple && props.appleUrl && (
                                <div ref={appleRef} style={{display:'flex', flexDirection:'column', alignItems:'center', gap:10, minWidth:320}}>
                                    <a href={props.appleUrl} aria-label="Add to Apple Wallet" onClick={props.onAppleDownload}>
                                        <img src={`${Settings.BaseUrl}/wallet-badges/apple.svg`} alt="Add to Apple Wallet" style={{height:28, transform:'scale(2.6)', transformOrigin:'center center', padding:'6px 0'}} />
                                    </a>
                                    <div style={{color:'#6B7280'}}>- or -</div>
                                    <div style={{border:'1px solid #D1D5DB', borderRadius:'8px', padding:'12px', display:'inline-block'}}>
                                        {/*@ts-ignore*/}
                                        <QRCode value={props.appleUrl} size={180}/>
                                    </div>
                                </div>
                            )}
                            {props.showApple && props.appleUrl && props.showGoogle && props.googleUrl && (
                                isStacked ? (
                                    <div style={{flexBasis:'100%', height:1, background:'#E5E7EB', margin:'8px 0'}} />
                                ) : (
                                    <div style={{width:1, background:'#E5E7EB', margin:'0 8px'}} />
                                )
                            )}
                            {props.showGoogle && props.googleUrl && (
                                <div ref={googleRef} style={{display:'flex', flexDirection:'column', alignItems:'center', gap:10, minWidth:320}}>
                                    <a href={props.googleUrl} aria-label="Save to Google Wallet" onClick={props.onGoogleDownload}>
                                        <img src={`${Settings.BaseUrl}/wallet-badges/google.svg`} alt="Save to Google Wallet" style={{height:28, transform:'scale(2.4)', transformOrigin:'center center', padding:'6px 0'}} />
                                    </a>
                                    <div style={{color:'#6B7280'}}>- or -</div>
                                    <div style={{border:'1px solid #D1D5DB', borderRadius:'8px', padding:'12px', display:'inline-block'}}>
                                        {/*@ts-ignore*/}
                                        <QRCode value={props.googleUrl} size={180}/>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
                )}
            </div>
        </div>
    );
}


